import type { ReactNode } from "react";
import { Link, Tooltip, TooltipFocusableTrigger } from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import { useTranslations } from "@zapier/i18n";

import type { CreateBlockReason } from "../useConnectionCreateRules";
import { useCreateBlockCopy } from "./useCreateBlockCopy";

export const CREATE_BLOCK_ICONS = {
  restricted: "actionLock",
  managed: "personSecurity",
} as const satisfies Record<CreateBlockReason, string>;

export const CREATE_BLOCK_ADMIN_LINKS: Record<CreateBlockReason, string> = {
  restricted: "/app/admin/app-permissions",
  managed: "/app/admin/app-connection-sharing",
};

interface Props {
  reason: CreateBlockReason;
  /** Rendered inside the tooltip trigger, before the icon (e.g. the app name). */
  children: ReactNode;
  triggerClassName?: string;
}

/** Locked row content: children plus an icon whose tooltip explains the block, and an admin link. */
export function ConnectionCreateBlocked({
  reason,
  children,
  triggerClassName,
}: Props) {
  const t = useTranslations("connection-create-rules");
  const { canManage, getMessage } = useCreateBlockCopy();

  return (
    <>
      <Tooltip content={getMessage(reason)} allowMultiline placement="top">
        <TooltipFocusableTrigger className={triggerClassName}>
          {children}
          <Icon
            name={CREATE_BLOCK_ICONS[reason]}
            size={16}
            color="GrayWarm6"
            aria-hidden={true}
          />
        </TooltipFocusableTrigger>
      </Tooltip>
      {canManage && (
        <Link
          href={CREATE_BLOCK_ADMIN_LINKS[reason]}
          target="_blank"
          variant="tertiary"
          size="compact"
          iconPosition="end"
        >
          {reason === "managed"
            ? t("buttons.manage-sharing")
            : t("buttons.manage-apps")}
          <Icon name="arrowOffsite" aria-hidden={true} />
        </Link>
      )}
    </>
  );
}
